import {
  createContext,
  useLayoutEffect,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { axiosInstance } from "../api/axiosInstance";
import { isAxiosError } from "axios";
import { safeJsonParse } from "../utils/api-utils";
import { PageLoader } from "../components/loaders";
import { Mixpanel } from "../utils/mixpanel";

export const AuthenticationContext = createContext({});

let refreshPromise = null;
const requestTokenRefresh = (token) => {
  if (!token) return Promise.resolve(null);
  if (!refreshPromise) {
    refreshPromise = axiosInstance
      .post("/auth/token/refresh", { token })
      .then((res) => ({
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
      }))
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const AuthenticationProvider = ({ children }) => {
  const getStoredItem = (key) => {
    try {
      const item = localStorage.getItem(key);
      return key === "user" ? safeJsonParse(item) : item;
    } catch {
      return null;
    }
  };

  const updateStoredItem = (key, value) => {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      const storageValue = key === "user" ? JSON.stringify(value) : value;
      localStorage.setItem(key, storageValue);
    }
  };

  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(() =>
    getStoredItem("refreshToken")
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(false);
  const [isAppInitialized, setIsAppInitialized] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const accessTokenRef = useRef(null);
  const refreshTokenRef = useRef(getStoredItem("refreshToken"));
  const hasInitializedRef = useRef(false);

  const isAuthenticated = !!accessToken;

  const updateTokens = (newAccessToken, newRefreshToken) => {
    localStorage.removeItem("access_token");
      localStorage.removeItem("accessToken");

    setAccessToken(newAccessToken || null);
    accessTokenRef.current = newAccessToken || null;

    if (newRefreshToken !== undefined) {
    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
    } else {
      localStorage.removeItem("refreshToken");
    }
      setRefreshToken(newRefreshToken || null);
      refreshTokenRef.current = newRefreshToken || null;
    }
  };

  const clearTokens = () => {
    updateTokens(null, null);
    setUser(null);
  };

  const refreshAccessToken = async (tokenOverride = null) => {
    const tokenToUse = tokenOverride || refreshTokenRef.current;
    if (!tokenToUse) return null;
    setIsRefreshing(true);
    try {
      const result = await requestTokenRefresh(tokenToUse);
      const newAccess = result?.accessToken;
      const newRefresh = result?.refreshToken ?? tokenToUse;
      updateTokens(newAccess, newRefresh);
      return newAccess;
    } catch {
      clearTokens();
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  useLayoutEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        const tokenToUse = accessTokenRef.current;
        if (config.skipAuthenticatedUser) {
          delete config.headers?.["x-authenticated-user"];
          return config;
        }
        if (tokenToUse) {
          config.headers["x-authenticated-user"] = `Bearer ${tokenToUse}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.config?.url?.includes("/auth/token/refresh")) {
          return Promise.reject(error);
        }
        const msg = error.response?.data?.message?.toLowerCase();
        const isExpiredToken =
          msg?.includes("x-authenticated-user") ||
          msg?.includes("unauthorized") ||
          null;
        if (error.response?.status === 401 && refreshTokenRef.current && isExpiredToken) {
          const newAccess = await refreshAccessToken();
          if (newAccess) {
            error.config.headers[
              "x-authenticated-user"
            ] = `Bearer ${newAccess}`;
            return axiosInstance(error.config);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && isGuestUser) {
      sessionStorage.removeItem("guest_user");
      setIsGuestUser(false);
    }
  }, [isAuthenticated]);

  useLayoutEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const initializeApp = async () => {
      try {
        await initializeAuth();
      } finally {
        setIsAppInitialized(true);
      }
    };
    initializeApp();
  }, []);

  useLayoutEffect(() => {
    const storedGuest = sessionStorage.getItem("guest_user");
    if (storedGuest && !accessToken) {
      setUser(JSON.parse(storedGuest));
      setIsGuestUser(true);
    }
  }, []);

  const makeApiRequest = async (apiCall, runOnClose) => {
    setIsLoading(true);
    try {
      const result = await apiCall();
      const data = result.data;
      return {
        success: data.status || true,
        data,
        message:
          typeof data.message === "object" ? data.message[0] : data.message,
      };
    } catch (err) {
      return {
        success: false,
        data: isAxiosError(err) ? err.response?.data : undefined,
        message:
          err.response?.data?.message || err.message || "Something went wrong",
      };
    } finally {
      setIsLoading(false);
      runOnClose?.();
    }
  };

  const createGuestUser = async (credentials, runOnClose) => {
    return makeApiRequest(async () => {
      const { email, ...guestCredentials } = credentials || {};
      const normalizedCredentials = email?.trim()
        ? { ...guestCredentials, email: email.trim() }
        : guestCredentials;
      const { data: respData } = await axiosInstance.post(
        "/create_guest_user",
        normalizedCredentials,
        {
          headers: {
            "guest-user-key": process.env.REACT_APP_API_GUEST_USER_KEY,
          },
        }
      );
      const guest = respData?.data;
      if (guest) {
        const enrichedGuest = {
          ...guest,
          ...(credentials?.pick_up_address
            ? { pick_up_address: credentials.pick_up_address }
            : {}),
          ...(credentials?.pick_up_location_id
            ? { pick_up_location_id: credentials.pick_up_location_id }
            : {}),
        };
        setUser(enrichedGuest);
        setIsGuestUser(true);
        sessionStorage.setItem("guest_user", JSON.stringify(enrichedGuest));
        Mixpanel.identify_guest({
          ...credentials,
          guest: enrichedGuest,
        });
      }
      return { data: respData };
    }, runOnClose);
  };

  const login = async (credentials, runOnClose) => {
    sessionStorage.removeItem("guest_user");
    setIsGuestUser(false);

    return makeApiRequest(async () => {
      const { data } = await axiosInstance.post("/auth/login", credentials);
      const {
        access_token: newAccessToken,
        refresh_token: newRefreshToken,
        userid: user,
      } = data;

      updateTokens(newAccessToken, newRefreshToken);

      const userInfo = await fetchUserInfo(newAccessToken);

      if (userInfo) {
        setUser(userInfo.data);
        Mixpanel.alias(userInfo.data.id || userInfo.data._id);
        Mixpanel.identify_user({
          id: userInfo.data.id || userInfo.data._id,
          email: userInfo.data.email,
          phone: userInfo.data.phone || userInfo.data.phonenumber,
          firstName: userInfo.data.firstname,
          lastName: userInfo.data.lastname,
          createdAt: userInfo.data.createdAt || userInfo.data.signup_date,
        });
      }
      const newData = { ...data, user: userInfo?.data };
      return { data: newData };
    }, runOnClose);
  };

  const logout = () => {
    Mixpanel.track("User Logged Out");
    Mixpanel.reset();

    clearTokens();
    sessionStorage.removeItem("guest_user");
    setIsGuestUser(false);
    setUser(null);
  };

  const initializeAuth = async () => {
    const storedRefresh = localStorage.getItem("refreshToken");
    if (storedRefresh) {
      refreshTokenRef.current = storedRefresh;
      const newAccess = await refreshAccessToken(storedRefresh);
      if (newAccess) {
        const userInfo = await fetchUserInfo(newAccess);
        if (userInfo) {
          setUser(userInfo.data);
          Mixpanel.identify_user({
            id: userInfo.data.id || userInfo.data._id,
            email: userInfo.data.email,
            phone: userInfo.data.phone || userInfo.data.phonenumber,
            firstName: userInfo.data.firstname || userInfo.data.firstName,
            lastName: userInfo.data.lastname || userInfo.data.lastName,
            createdAt: userInfo.data.createdAt || userInfo.data.signup_date,
          });
          Mixpanel.track("Session Restored", {
            user_id: userInfo.data.id,
          });
          return true;
        }
      }
    }

      const storedGuest = sessionStorage.getItem("guest_user");
      if (storedGuest) {
        const guestData = JSON.parse(storedGuest);
        setUser(guestData);
        setIsGuestUser(true);
        Mixpanel.identify_guest(guestData);
      }
    return false;
  };

  const fetchUserInfo = async (tokenToUse = null) => {
    const currentToken = tokenToUse || accessToken;

    if (!currentToken) return null;

    try {
      const { data } = await axiosInstance.get("/profile", {
        headers: {
          "x-authenticated-user": `Bearer ${currentToken}`,
        },
      });
      return data;
    } catch (err) {
      console.error("Failed to fetch user info:", err);
      return null;
    }
  };

  const apiEndpoints = {
    register: (formInfo) => axiosInstance.post("/auth/register", formInfo),
    activateAccount: (formInfo) =>
      axiosInstance.post("/auth/activate_account", formInfo),
    resendOtp: (formInfo) => axiosInstance.post("/auth/resend_otp", formInfo),
    forgotPassword: (formInfo) =>
      axiosInstance.post("/auth/forgot_password", formInfo),
    resetPassword: (formInfo) =>
      axiosInstance.post("/auth/reset_password", formInfo),
  };

  const createApiMethod = (endpoint) => (formInfo, runOnClose) => makeApiRequest(() => apiEndpoints[endpoint](formInfo), runOnClose);
  // const createApiMethod = (lambda x:)
  
  const register = createApiMethod("register");
  const activateAccount = createApiMethod("activateAccount");
  const resendOtp = createApiMethod("resendOtp");
  const forgotPassword = createApiMethod("forgotPassword");
  const resetPassword = createApiMethod("resetPassword");

  if (!isAppInitialized) return <PageLoader />;

  return (
    <AuthenticationContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        isGuestUser,
        isAppInitialized,
        isRefreshing,
        user,
        login,
        logout,
        register,
        setAccessToken,
        activateAccount,
        resendOtp,
        forgotPassword,
        resetPassword,
        fetchUserInfo,
        setUser,
        initializeAuth,
        createGuestUser,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = () => useContext(AuthenticationContext);
